import i18next from 'i18next';

export default function initI18n(): void {
  i18next.addResourceBundle('en', 'video-ui', {
    channelDetailsTab: 'Channel Details',
    resourceLinksTab: 'Resource Links',
    editDescription: 'Edit Description',
    saveDescription: 'Save Description',
    publishSelectedChannels: 'Publish selected channels',
    unpublishSelectedChannels: 'Unpublish selected channels',
    clearWarningsForSelectedChannels: 'Clear warnings for selected channels',
    deleteVideosForSelectedChannels: 'Delete videos for selected channels',
    deleteSelectedChannels: 'Delete selected channels',
    editChannel: 'Edit channel',
    addVideos: 'Add videos',
    reviewVideos: 'Review videos',
    publishAllVideos: 'Publish all videos',
    unpublishAllVideos: 'Unpublish all videos',
    clearWarnings: 'Clear warnings',
    repairLastVideoDate: 'Repair last video date',
    deleteVideos: 'Delete videos',
    deleteChannel: 'Delete channel',
    searchQuery: 'Search Query',
    searchQueryPlaceholder: 'Enter search term...',
    searchYouTubePageTitle: 'Search YouTube',
    addChannelButtonTitle: 'Add Channel',
    addVideosButtonTitle: 'Add Videos',
    videoDetailsTab: 'Video Details',
    publishSelectedVideos: 'Publish selected videos',
    unpublishSelectedVideos: 'Unpublish selected videos',
    clearWarningsForSelectedVideos: 'Clear warnings for selected videos',
    reindexSelectedVideos: 'Reindex selected videos',
    deleteSelectedVideos: 'Delete selected videos',
    addMoreVideos: 'Add more videos',
    reindexVideo: 'Reindex video',
    deleteVideo: 'Delete video',

    // Generic reusable action words / nouns
    cancel: 'Cancel',
    delete: 'Delete',
    publish: 'Publish',
    unpublish: 'Unpublish',
    reindex: 'Reindex',
    clearWarningsFor: 'Clear Warnings for',
    channelNoun: 'Channel',
    channelsNoun: 'Channels',
    videoNoun: 'Video',
    videosNoun: 'Videos',

    // Description panels (ChannelDetailPanel / VideoDetailPanel)
    descriptionUpdatedSuccessfully: 'Description updated successfully',

    // Single-row toggle undo snacks
    channelPublished: 'Channel published',
    channelUnpublished: 'Channel unpublished',
    videoPublished: 'Video published',
    videoUnpublished: 'Video unpublished',

    // Single-row misc snacks
    warningsCleared: 'Warnings cleared',
    lastVideoDateRepaired: 'Last video date repaired',
    videoReindexingJobStarted: 'Video reindexing job started',
    youtubeChannelIndexingJobStarted: 'YouTube channel indexing job started',
    failedToUpdateChannel: 'Failed to update channel',

    // Add / review videos dialogs
    numberOfVideosToIndexDescription: 'Enter the number of videos to index.',
    numberOfVideosToReviewDescription: 'Enter the number of videos to review.',
    channelIdOptionalLabel: 'Channel ID (optional)',
    enterYoutubeChannelIdDescription: 'Enter the YouTube channel ID',
    channelIdAndNumberToIndexDescription:
      'Enter the ID of the Channel to index videos for, and how many videos to index.',

    startedIndexingVideosCount: 'Started indexing {{count}} video',
    startedIndexingVideosCount_plural: 'Started indexing {{count}} videos',
    startedReviewingVideosCount: 'Started reviewing {{count}} video',
    startedReviewingVideosCount_plural: 'Started reviewing {{count}} videos',
    startedIndexingYoutubeVideosCount: 'Started indexing {{count}} YouTube video',
    startedIndexingYoutubeVideosCount_plural: 'Started indexing {{count}} YouTube videos',
    startedReindexingJobForVideosCount: 'Started reindexing job for {{count}} video',
    startedReindexingJobForVideosCount_plural: 'Started reindexing job for {{count}} videos',

    // Delete Channel Videos confirm (single channel)
    deleteChannelVideosConfirmTitle: 'Delete Channel Videos',
    deleteChannelVideosConfirmDescription:
      'Are you sure you want to delete all {{count}} videos for "{{title}}"? This action cannot be undone.',

    // Publish / unpublish all videos for a channel confirm
    publishVideosConfirmTitle: 'Publish {{count}} Video',
    publishVideosConfirmTitle_plural: 'Publish {{count}} Videos',
    publishVideosConfirmDescription:
      'Are you sure you want to publish {{count}} video for "{{title}}" with status "review" and no warnings.',
    publishVideosConfirmDescription_plural:
      'Are you sure you want to publish {{count}} videos for "{{title}}" with status "review" and no warnings.',
    unpublishVideosConfirmTitle: 'Unpublish {{count}} Video',
    unpublishVideosConfirmTitle_plural: 'Unpublish {{count}} Videos',
    unpublishVideosConfirmDescription:
      'Are you sure you want to unpublish {{count}} video for "{{title}}"?',
    unpublishVideosConfirmDescription_plural:
      'Are you sure you want to unpublish {{count}} videos for "{{title}}"?',

    noReviewVideosWithoutWarningsFound:
      'No videos found with status "review" and no warnings for "{{title}}"',
    noPublishedVideosFoundForChannel: 'No published videos found for "{{title}}"',
    videosPublishedForChannel: '{{count}} video published for "{{title}}"',
    videosPublishedForChannel_plural: '{{count}} videos published for "{{title}}"',
    videosUnpublishedForChannel: '{{count}} video unpublished for "{{title}}"',
    videosUnpublishedForChannel_plural: '{{count}} videos unpublished for "{{title}}"',

    // Delete Channel confirm
    deleteChannelConfirmTitle: 'Delete Channel',
    deleteChannelConfirmDescription:
      'Are you sure you want to delete "{{title}}" and all associated videos? This action cannot be undone.',

    // Group delete channels
    deleteChannelsVideosDescriptionSuffix:
      ' This will also delete all their videos. This action cannot be undone.',

    // Group toggle published counts (channels / videos)
    channelDeleted: 'Channel deleted',
    videoDeleted: 'Video deleted',
    videosDeletedCount: '{{count}} video deleted',
    videosDeletedCount_plural: '{{count}} videos deleted',
    channelsDeletedCount: '{{count}} channel deleted',
    channelsDeletedCount_plural: '{{count}} channels deleted',
    channelsPublishedCount: '{{count}} channel published',
    channelsPublishedCount_plural: '{{count}} channels published',
    channelsUnpublishedCount: '{{count}} channel unpublished',
    channelsUnpublishedCount_plural: '{{count}} channels unpublished',
    videosPublishedCount: '{{count}} video published',
    videosPublishedCount_plural: '{{count}} videos published',
    videosUnpublishedCount: '{{count}} video unpublished',
    videosUnpublishedCount_plural: '{{count}} videos unpublished',

    // Group clear warnings counts
    warningsClearedForChannelsCount: 'Warnings cleared for {{count}} channel',
    warningsClearedForChannelsCount_plural: 'Warnings cleared for {{count}} channels',
    warningsClearedForVideosCount: 'Warnings cleared for {{count}} video',
    warningsClearedForVideosCount_plural: 'Warnings cleared for {{count}} videos',

    // Group delete videos for channels
    deleteVideosFor: 'Delete Videos for',
    deleteVideosForMatchingChannelsTitle: 'Delete videos for all {{count}} matching Channel',
    deleteVideosForMatchingChannelsTitle_plural:
      'Delete videos for all {{count}} matching Channels',
    deleteVideosForSelectedChannelsTitle: 'Delete videos for {{count}} selected Channel',
    deleteVideosForSelectedChannelsTitle_plural: 'Delete videos for {{count}} selected Channels',
    deleteVideosForMatchingChannelsDescription:
      'Are you sure you want to delete all videos for all {{count}} matching channel? This action cannot be undone.',
    deleteVideosForMatchingChannelsDescription_plural:
      'Are you sure you want to delete all videos for all {{count}} matching channels? This action cannot be undone.',
    deleteVideosForSelectedChannelsDescription:
      'Are you sure you want to delete all videos for the {{count}} selected channel? This action cannot be undone.',
    deleteVideosForSelectedChannelsDescription_plural:
      'Are you sure you want to delete all videos for the {{count}} selected channels? This action cannot be undone.',
    videosDeletedForChannelsCount: 'Videos deleted for {{count}} channel',
    videosDeletedForChannelsCount_plural: 'Videos deleted for {{count}} channels',

    // Delete single video confirm
    deleteVideoConfirmTitle: 'Delete Video',
    deleteVideoConfirmDescription:
      'Are you sure you want to delete "{{title}}"? This action cannot be undone.',

    // Group delete videos
    deleteVideosDescriptionSuffix: ' This action cannot be undone.',
  });
}
